import { useCallback, useMemo, useState } from 'react'

import {
  IconArrowsSort,
  IconCaretDown,
  IconSortAscending,
  IconSortAscending2,
  IconSortAscendingLetters,
  IconSortAscendingNumbers,
  IconSortDescending,
  IconSortDescending2,
  IconSortDescendingLetters,
  IconSortDescendingNumbers,
} from '@tabler/icons-react'
import styled from 'styled-components'

import { Popover, PopoverItem } from '../shared-components/BUI/Popover'

import { useFilters } from '@/context/filterContext'
import { useListViewInfo } from '@/context/listViewContext'
import { useQueryParams } from '@/context/queryParamsContext'
import { Params } from '@/utils/params'
import { pluraliseWord } from '@/utils/pluralise'

import { buildPassTheme } from 'src/styles/theme'

import { SortOrder } from 'types/api.graphql'

export const SortWrapper = styled.span`
  ${({ theme }) => theme.font.label02}
  font-weight: 600;
  color: ${({ theme }) => theme.colour.secondary.base};
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
`

const Desc = styled.span`
  ${({ theme }) => theme.font.label02}
  color: ${({ theme }) => theme.colour.secondary._500};
  margin-left: auto;
`

export type SortOption<T extends object = Record<string, string>> = {
  label: string
  direction: SortOrder
  key: keyof T
  description?: string
  default?: boolean
  type?: 'text' | 'number' | 'box'
}

export const getSortIcon = (sortOption: SortOption) => {
  if (sortOption.type === 'box') {
    return sortOption.direction === SortOrder.Asc ? (
      <IconSortAscending2 size={16} />
    ) : (
      <IconSortDescending2 size={16} />
    )
  }

  if (sortOption.type === 'number') {
    return sortOption.direction === SortOrder.Asc ? (
      <IconSortAscendingNumbers size={16} />
    ) : (
      <IconSortDescendingNumbers size={16} />
    )
  }

  if (sortOption.type === 'text') {
    return sortOption.direction === SortOrder.Asc ? (
      <IconSortAscendingLetters size={16} />
    ) : (
      <IconSortDescendingLetters size={16} />
    )
  }

  return sortOption.direction === SortOrder.Asc ? (
    <IconSortAscending size={16} />
  ) : (
    <IconSortDescending size={16} />
  )
}

export const getCurrentSortDisplay = (
  isMobile: boolean,
  sortOption?: SortOption
) => {
  if (isMobile) return 'Sort'

  if (!sortOption) return 'Sort by'

  if (sortOption.description) {
    return `${sortOption.label} (${sortOption.description})`
  }

  return sortOption.label
}

type Props = {
  isMobile: boolean
  sortOptions?: SortOption[]
  compact?: boolean
  useQuery?: boolean
}

export const CorePageListViewSort: React.FC<Props> = ({
  isMobile,
  sortOptions,
  compact,
  useQuery,
}) => {
  const { params, updateParams } = useQueryParams<Params.Sort>()

  const { routeFilters, updateFilters } = useFilters()
  const { resourceSingular } = useListViewInfo()
  const [sortOpen, toggleOpen] = useState(false)

  const currentSortOption = useMemo(() => {
    let sortKey: string
    let direction: SortOrder

    if (useQuery) {
      sortKey = params.sortKey
      direction = params.direction
    } else {
      const sort = routeFilters?.sort || {}
      sortKey = sort.sortKey
      direction = sort.direction
    }

    if (!sortKey) {
      const defaultOption = sortOptions?.find((o) => o.default)
      return defaultOption
    }

    return sortOptions.find(
      (o) => o.key === sortKey && o.direction === direction
    )
  }, [
    routeFilters?.sort,
    sortOptions,
    useQuery,
    params.sortKey,
    params.direction,
  ])

  const applySort = useCallback(
    (sortOption: SortOption) => {
      if (useQuery) {
        const defaultSortOption = sortOptions?.find((o) => o.default)
        if (
          defaultSortOption.key === sortOption.key &&
          defaultSortOption.direction === sortOption.direction
        ) {
          updateParams({ sortKey: null, direction: null }) // clear it if it's the default
        } else {
          updateParams({
            sortKey: sortOption.key,
            direction: sortOption.direction,
          })
        }
      } else {
        updateFilters({
          filters: null,
          sort: { sortKey: sortOption.key, direction: sortOption.direction },
        })
      }
      toggleOpen(false)
    },
    [updateFilters, useQuery, updateParams, sortOptions]
  )

  const menu = useMemo(() => {
    return sortOptions?.map((option, index) => (
      <PopoverItem
        key={option.key + index}
        onClick={() => applySort(option)}
        active={
          currentSortOption?.key === option.key &&
          currentSortOption?.description === option.description
        }
      >
        {getSortIcon(option)}
        {option.label}
        {option.description && <Desc>({option.description})</Desc>}
      </PopoverItem>
    ))
  }, [sortOptions, applySort, currentSortOption])

  if (!sortOptions) return null

  return (
    <Popover
      placement="bottomRight"
      showArrow={false}
      content={menu}
      open={sortOpen}
      onOpenChange={toggleOpen}
      title={`Sort ${pluraliseWord(2, resourceSingular)} by`}
    >
      {compact ? (
        <SortWrapper>
          <IconArrowsSort size={16} />
          <IconCaretDown
            size={16}
            fill={buildPassTheme.colour.secondary.base}
          />
        </SortWrapper>
      ) : (
        <SortWrapper>
          {getCurrentSortDisplay(isMobile, currentSortOption)}
          <IconCaretDown
            size={16}
            fill={buildPassTheme.colour.secondary.base}
          />
        </SortWrapper>
      )}
    </Popover>
  )
}
